import React, { useState, useEffect } from 'react';
import { Clock, TrendingUp, DollarSign, Users, Activity } from 'lucide-react';
import { db } from '../../db';
import { formatZAR } from '../../utils/formatters';

const DashboardWidgets = ({ role, userMetadata }) => {
    const [stats, setStats] = useState({
        commission: 0,
        pipeline: 0,
        trialDays: 0,
        systemHealth: 'Online',
        clients: 0
    });

    useEffect(() => {
        loadStats();
    }, [role, userMetadata?.email, userMetadata?.Email, userMetadata?.UserID]);

    const loadStats = async () => {
        // 1. Trial Timer (Client)
        if (role === 'CLIENT') {
            const startStr = userMetadata?.trialStart || userMetadata?.Trial_Start_Date;
            const start = startStr ? new Date(startStr) : new Date();
            const end = new Date(start);
            end.setDate(end.getDate() + 14); // 14 Day Trial
            const diff = end - new Date();
            const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
            setStats(prev => ({ ...prev, trialDays: days > 0 ? days : 0 }));
        }

        // 2. Commissions & Pipeline (Partner/Coach/Admin)
        if (role === 'COACH' || role === 'PARTNER' || role === 'ADMIN') {
            try {
                // --- Commission Calculation ---
                const ledger = await db.tabData.where('tabName').equals('Commission_Ledger').first();
                if (ledger?.data) {
                    const unpaid = ledger.data
                        .filter(row => row.Status !== 'Paid' && (row.Coach_ID === userMetadata.UserID || row.Partner_Email === userMetadata.Email || row.Partner_Email === userMetadata.email))
                        .reduce((sum, row) => sum + (parseFloat(row.Commission_Earned) || 0), 0);
                    setStats(prev => ({ ...prev, commission: unpaid }));
                }

                // --- Pipeline Calculation ---
                const dealsTab = await db.tabData.where('tabName').equals('CRM_Deals').first();
                if (dealsTab?.data) {
                    const userEmail = userMetadata.Email || userMetadata.email;
                    const activePipeline = dealsTab.data
                        .filter(deal =>
                            (deal.Owner_Email === userEmail) &&
                            (deal.Status !== 'Closed' && deal.Status !== 'Lost')
                        )
                        .reduce((sum, deal) => sum + (parseFloat(deal.Deal_Value) || 0), 0);
                    setStats(prev => ({ ...prev, pipeline: activePipeline }));
                }

                // --- Clients Calculation ---
                const usersTab = await db.tabData.where('tabName').equals('Users').first();
                if (usersTab?.data) {
                    const userId = userMetadata.UserID;
                    const userEmail = userMetadata.Email || userMetadata.email;
                    const clients = usersTab.data.filter(user => {
                        // For Coaches: count users assigned to them
                        if (role === 'COACH') {
                            return user.Assigned_Coach_ID === userId;
                        }
                        // For Partners: count users referred by them
                        if (role === 'PARTNER') {
                            return user.Referred_By_Email === userEmail;
                        }
                        return false;
                    }).length;
                    setStats(prev => ({ ...prev, clients }));
                }
            } catch (e) { console.error("Stats Load Error", e); }
        }
    };

    if (role === 'CLIENT') {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-6 text-white shadow-lg shadow-blue-200 relative overflow-hidden">
                    <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-2 opacity-80">
                            <Clock size={16} /> <span className="text-xs font-bold uppercase tracking-widest">Trial Period</span>
                        </div>
                        <h2 className="text-4xl font-black mb-1">{stats.trialDays} Days</h2>
                        <p className="text-sm font-medium opacity-90">Remaining in your free trial.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (role === 'COACH' || role === 'PARTNER' || role === 'ADMIN') {
        return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {/* Commission Widget */}
                <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-green-100 p-2 rounded-xl text-green-600"><DollarSign size={20} /></div>
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pending Payout</span>
                    </div>
                    <h2 className="text-3xl font-black text-slate-800">{formatZAR(stats.commission)}</h2>
                    <p className="text-xs text-slate-400 mt-2">Next payout run: 25th</p>
                </div>

                {/* Pipeline Widget */}
                <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-purple-100 p-2 rounded-xl text-purple-600"><TrendingUp size={20} /></div>
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pipeline Value</span>
                    </div>
                    <h2 className="text-3xl font-black text-slate-800">{formatZAR(stats.pipeline)}</h2>
                    <p className="text-xs text-slate-400 mt-2">Active deals in CRM</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-blue-100 p-2 rounded-xl text-blue-600"><Users size={20} /></div>
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Clients</span>
                    </div>
                    <h2 className="text-3xl font-black text-slate-800">{stats.clients}</h2>
                    <p className="text-xs text-slate-400 mt-2">Active clients in your network</p>
                </div>
            </div>
        );
    }

    if (role === 'ADMIN') {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="bg-slate-800 text-white rounded-3xl p-6 shadow-lg relative overflow-hidden">
                    <div className="flex items-center gap-3 mb-4">
                        <Activity className="text-green-400" />
                        <span className="font-bold">System Health</span>
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between text-sm"><span className="opacity-60">n8n Workflows</span> <span className="text-green-400 font-bold">Operational</span></div>
                        <div className="flex justify-between text-sm"><span className="opacity-60">Database</span> <span className="text-green-400 font-bold">Synced</span></div>
                        <div className="flex justify-between text-sm"><span className="opacity-60">Webhooks</span> <span className="text-green-400 font-bold">Active</span></div>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default DashboardWidgets;
